import { useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export interface FilterOption {
  value: string;
  label: string;
}

export interface FilterBarProps {
  filters: {
    value: string;
    onValueChange: (value: string) => void;
    options: FilterOption[];
    placeholder: string;
    searchPlaceholder?: string;
    width?: string;
  }[];
  className?: string;
}

/**
 * FilterBar - Modular component for displaying filter dropdowns
 * 
 * Uses the exact same styling as DataTable's placeholder filter section.
 * Matches the h-9 bg-muted rounded-md pattern from the URLs screen.
 * 
 * Usage:
 * <FilterBar
 *   filters={[
 *     {
 *       value: statusFilter,
 *       onValueChange: setStatusFilter,
 *       options: [...],
 *       placeholder: "All Statuses"
 *     },
 *     ...
 *   ]}
 * />
 */
export function FilterBar({ filters, className }: FilterBarProps) {
  if (filters.length === 0) return null;

  return (
    <div className={cn("px-6 pb-4 flex gap-3 flex-wrap", className)}>
      {filters.map((filter, idx) => (
        <FilterDropdown key={idx} {...filter} />
      ))}
    </div>
  );
}

/**
 * FilterDropdown - Individual filter dropdown matching DataTable placeholder styling
 */
function FilterDropdown({
  value,
  onValueChange,
  options,
  placeholder,
  width,
}: {
  value: string;
  onValueChange: (value: string) => void;
  options: FilterOption[];
  placeholder: string;
  searchPlaceholder?: string;
  width?: string;
}) {
  const [open, setOpen] = useState(false);
  const selectedOption = options.find((option) => option.value === value);

  return (
    <div className={cn("flex-1 min-w-[120px]", width)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="h-9 w-full bg-muted rounded-md flex items-center justify-between px-3 cursor-pointer hover:bg-muted/80 transition-colors border-0 outline-none focus:outline-none focus:ring-0 focus-visible:ring-0 focus-visible:outline-none"
          >
            <span className="text-xs text-muted-foreground">
              {selectedOption ? selectedOption.label : placeholder}
            </span>
            <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[200px] p-1" align="start">
          <div className="flex flex-col gap-0.5">
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  onValueChange(option.value);
                  setOpen(false);
                }}
                className={cn(
                  "flex items-center px-2 py-1.5 text-sm rounded-sm hover:bg-accent hover:text-accent-foreground cursor-pointer transition-colors",
                  value === option.value && "bg-accent"
                )}
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    value === option.value ? "opacity-100" : "opacity-0"
                  )}
                />
                {option.label}
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}


